import React, { useEffect, useState } from 'react';
import i18n from '@/utils/i18n';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import ContainerTypeSelector from '../../components/ContainerType/ContainerTypeSelector';
import { useCreateContainerQrRedirectionCampaignForContainerTypeMutation } from '../../graphql/mutations/createContainerQrRedirectionCampaignForContainerType.generated';
import {
  Input,
  InputGroup,
  InputGroupText,
  Button,
  FormFeedback,
  Form,
  Alert,
} from 'reactstrap';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { LoadingContainer } from '../../components/Layout';

const checkIsRedirectUrlValid = (redirectUrl: string) => {
  if (redirectUrl.length === 0) {
    return true;
  }

  try {
    return Boolean(new URL(redirectUrl));
  } catch (e) {
    return false;
  }
};

type AddContainerQrRedirectionCampaignFormType = {
  containerTypes: ContainerType[];
  setRefresh: (status: boolean) => void;
};

const AddContainerQrRedirectionCampaignForm = ({
  containerTypes,
  setRefresh,
}: AddContainerQrRedirectionCampaignFormType) => {
  const currentDate = new Date();
  const futureDate = new Date(currentDate.getTime() + 30 * 60000);

  const ERROR_TO_STRING: Record<string, string> = {
    DATE_IS_IN_THE_PAST: i18n.t(
      'ContainerQrRedirectionCampaign:errorDateInPast',
    ),
    STARTDATE_HAS_TO_BE_BEFORE_ENDDATE: i18n.t(
      'ContainerQrRedirectionCampaign:errorStartDateAfterEndDate',
    ),
    CONTAINER_QR_REDIRECT_REFERENCE_CONTAINER_NOT_FOUND: i18n.t(
      'ContainerQrRedirectionCampaign:errorReferenceContainerNotFound',
    ),
    CONTAINER_QR_REDIRECTION_CAMPAIGNS_OVERLAPPING: i18n.t(
      'ContainerQrRedirectionCampaign:errorCampaignsOverlapping',
    ),
    CONTAINER_QR_REDIRECT_INSERT_FAILED: i18n.t(
      'ContainerQrRedirectionCampaign:errorInsertFailed',
    ),
    CONTAINER_TYPE_ID_MISSING: i18n.t(
      'ContainerQrRedirectionCampaign:errorContainerTypeIdMissing',
    ),
  };

  const [searchContainerTypeId, setSearchContainerTypeId] =
    useState<number>(-1);

  const [redirectUrl, setRedirectUrl] = useState('');
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(futureDate);
  const [comment, setComment] = useState('');
  const [
    createContainerQrRedirectionCampaignForContainerType,
    { data, error, loading },
  ] = useCreateContainerQrRedirectionCampaignForContainerTypeMutation();

  const isRedirectUrlValid = checkIsRedirectUrlValid(redirectUrl);

  useEffect(() => {
    setRefresh(true);
  }, [data]);

  const onSubmit = () => {
    createContainerQrRedirectionCampaignForContainerType({
      variables: {
        type_id: searchContainerTypeId,
        container_qr_redirects: {
          redirect_url: redirectUrl,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          comment,
        },
      },
    });
  };

  return (
    <>
      <Form>
        <div className="d-flex flex-wrap">
          {error && (
            <Alert color="danger">
              {ERROR_TO_STRING[error.message] || error.message}
            </Alert>
          )}
          <div className="input-group mb-3 flex-nowrap">
            <span className="input-group-text">
              {i18n.t('ContainerQrRedirectionCampaign:containerType')}
            </span>
            <ContainerTypeSelector
              className="form-control"
              data={containerTypes}
              value={searchContainerTypeId?.toString()}
              onChange={function (value: string | string[] | null): void {
                setSearchContainerTypeId(Number(value));
              }}
              searchable
              clearable
              required
            />
          </div>
          <div className="input-group mb-3 flex-nowrap">
            <InputGroup>
              <InputGroupText>
                {i18n.t('ContainerQrRedirectionCampaign:redirectUrl')}
              </InputGroupText>
              <Input
                value={redirectUrl}
                invalid={!isRedirectUrlValid}
                onChange={(event) => {
                  setRedirectUrl(event.currentTarget.value);
                }}
                required
              />
              {!isRedirectUrlValid && (
                <FormFeedback tooltip>
                  {i18n.t(
                    'ContainerQrRedirectionCampaign:errorRedirectUrlIsInvalid',
                  )}
                </FormFeedback>
              )}
            </InputGroup>
          </div>
          <div className="input-group mb-3 flex-nowrap">
            <span className="input-group-text">
              {i18n.t('ContainerQrRedirectionCampaign:startDate')}
            </span>

            <Input
              type="datetime-local"
              invalid={endDate < startDate}
              value={convertDateToDateTimeLocalString(startDate)}
              onChange={(event) => {
                setStartDate(new Date(event.target.value));
              }}
            />
            {endDate < startDate && (
              <FormFeedback tooltip>
                {i18n.t(
                  'ContainerQrRedirectionCampaign:errorStartDateAfterEndDate',
                )}
              </FormFeedback>
            )}
          </div>
          <div className="input-group mb-3 flex-nowrap">
            <span className="input-group-text">
              {i18n.t('ContainerQrRedirectionCampaign:endDate')}
            </span>

            <Input
              type="datetime-local"
              invalid={endDate < currentDate}
              value={convertDateToDateTimeLocalString(endDate)}
              onChange={(event) => {
                setEndDate(new Date(event.target.value));
              }}
            />
            {endDate < currentDate && (
              <FormFeedback tooltip>
                {i18n.t('ContainerQrRedirectionCampaign:errorDateInPast')}
              </FormFeedback>
            )}
          </div>

          <div className="input-group mb-3 flex-nowrap">
            <span className="input-group-text">
              {i18n.t('ContainerQrRedirectionCampaign:comment')}
            </span>

            <Input
              type="textarea"
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </div>

          {loading && <LoadingContainer />}
          {!loading && (
            <Button
              color="primary"
              onClick={() => {
                onSubmit();
              }}
            >
              {i18n.t('Common:add')}
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default AddContainerQrRedirectionCampaignForm;
